import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import ReactGA from "react-ga4";

// Components
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";

import ScrollToTop from "./routes.js/Scrolltop";
import LoadingSpinner from "./components/common/LoadingSpinner";
import MinivelWhy from "./pages/About/MinivelWhy";
import ChatBot from "./components/ChatBot/ChatBot";

// Initialize GA4
const GA_TRACKING_ID = "G-XXXXXXXXXX"; // Replace with your Measurement ID
ReactGA.initialize(GA_TRACKING_ID);

// ====================
// Lazy-loaded Pages
// ====================

// Main pages
import LandingPage from "./pages/LandingPage/LandingPage";
// const LandingPage = lazy(() => import("./pages/LandingPage/LandingPage"));
const About = lazy(() => import("./pages/About/About"));
const Gallery = lazy(() => import("./Gallery/Gallery"));
const Careers = lazy(() => import("./pages/Career/Careers"));
const JobDetails = lazy(() => import("./pages/Career/JobDetails"));
const JobAdmin = lazy(() => import("./pages/Career/JobAdmin"));
const AdminApplicants = lazy(() => import("./pages/Career/AdminApplicants"));
const Employer = lazy(() => import("./pages/Employer/Employer"));
const ContactPage = lazy(() => import("./pages/Contact/ContactPage"));
const ClientelePage = lazy(() => import("./pages/Clientpage/ClientelePage.jsx"));
const Services = lazy(() => import("./pages/Servicepage/Services"));

// Industries
const Industries = lazy(() => import("./pages/Industries/Industries"));
const Technology = lazy(() => import("./pages/IndustriesPage/Technology"));
const Engineering = lazy(() => import("./pages/IndustriesPage/Engineering"));
const Healthcare = lazy(() => import("./pages/IndustriesPage/Healthcare"));
const Finance = lazy(() => import("./pages/IndustriesPage/Finance"));
const Retail = lazy(() => import("./pages/IndustriesPage/Retail-&-Fmcg"));
const Manufacturing = lazy(() => import("./pages/IndustriesPage/Manufacturing"));
const Logistics = lazy(() => import("./pages/IndustriesPage/Logistics"));
const Energy = lazy(() => import("./pages/IndustriesPage/Energy"));

// Services
const TemporaryStaffing = lazy(() =>
  import("./pages/ServicesProvide/TemporaryStaffing")
);
const PermanentRecruitment = lazy(() =>
  import("./pages/ServicesProvide/PermanentRecruitment")
);
const RPO = lazy(() => import("./pages/ServicesProvide/RPO"));
const PayrollManagement = lazy(() =>
  import("./pages/ServicesProvide/PayrollManagement")
);
const Contract = lazy(() => import("./pages/ServicesProvide/Contract"));
const ComplianceSupport = lazy(() =>
  import("./pages/ServicesProvide/ComplianceSupport")
);

// Blogs (New Corporate Blog System)
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const BlogCategory = lazy(() => import("./pages/BlogCategory")); // optional route

// Details Pages (Hero CTA)
const Transformation = lazy(() => import("./pages/Details/Transformation"));
const AgileWorkforce = lazy(() => import("./pages/Details/AgileWorkforce"));
const Innovation = lazy(() => import("./pages/Details/Innovation"));

// Events
const Events = lazy(() => import("./pages/Events/Events"));



// =========================
// App Component
// =========================

export default function App() {
  const location = useLocation();

  // Track Page Views
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);

  // Preload frequently visited pages after first render
  useEffect(() => {
    const preload = () => {
      import("./pages/Career/Careers").catch(() => {});
      import("./pages/Contact/ContactPage.jsx").catch(() => {});
      import("./pages/About/About.jsx").catch(() => {});
      import("./pages/Clientpage/ClientelePage.jsx").catch(() => {});
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(preload, { timeout: 2000 });
    } else {
      setTimeout(preload, 1500);
    }
  }, []);


  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-900">


      {/* SEO Defaults */}
      <Helmet>
        <title>Minivel Services Pvt. Ltd.</title>
        <meta
          name="description"
          content="Minivel Services — India’s trusted HR consulting and recruitment partner providing staffing, payroll, RPO, and compliance services."
        />
        <link rel="icon" href="/favicon_io/favicon-32x32.png" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* Navbar */}
      <Navbar />

      {/* Page Animations + Route Wrapper */}
      <main className="flex-grow">
        <Suspense fallback={<LoadingSpinner />}>
          <ScrollToTop />
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={location.pathname === "/" ? "" : "pt-[100px] md:pt-[140px]"}
            >
              <Routes location={location} key={location.pathname}>
                {/* Main Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/careers/:id" element={<JobDetails />} />
                <Route path="/careers/admin" element={<JobAdmin />} />
                <Route path="/careers/applicants" element={<AdminApplicants />} />
                <Route path="/employer" element={<Employer />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/clients" element={<ClientelePage />} />
                <Route path="/minivel-why" element={<MinivelWhy />} />

                {/* Industries */}
                <Route path="/industries" element={<Industries />} />
                <Route path="/industries/technology" element={<Technology />} />
                <Route path="/industries/engineering" element={<Engineering />} />
                <Route path="/industries/healthcare" element={<Healthcare />} />
                <Route path="/industries/finance" element={<Finance />} />
                <Route
                  path="/industries/retail-and-fmcg"
                  element={<Retail />}
                />
                <Route
                  path="/industries/manufacturing"
                  element={<Manufacturing />}
                />
                <Route path="/industries/logistics" element={<Logistics />} />
                <Route path="/industries/energy" element={<Energy />} />

                {/* Services */}
                <Route path="/services" element={<Services />} />
                <Route
                  path="/services/temporarystaffing"
                  element={<TemporaryStaffing />}
                />
                <Route
                  path="/services/permanentrecruitment"
                  element={<PermanentRecruitment />}
                />
                <Route
                  path="/services/contractstaffing"
                  element={<Contract />}
                />
                <Route
                  path="/services/payrollmanagement"
                  element={<PayrollManagement />}
                />
                <Route path="/services/rpo" element={<RPO />} />
                <Route
                  path="/services/compliancesupport"
                  element={<ComplianceSupport />}
                />

                {/* ====================== */}
                {/*   NEW BLOG ROUTES     */}
                {/* ====================== */}
                <Route path="/blogs" element={<BlogList />} />
                <Route path="/blogs/:id" element={<BlogDetail />} />
                <Route path="/blogs/category/:slug" element={<BlogCategory />} />

                {/* Details Pages */}
                <Route path="/transformation" element={<Transformation />} />
                <Route path="/agile-workforce" element={<AgileWorkforce />} />
                <Route path="/innovation" element={<Innovation />} />

                {/* Events */}
                <Route path="/events" element={<Events />} />

                {/* Fallback */}
                <Route path="*" element={<LandingPage />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </main>

      {/* Global Interactive ChatBot */}
      <ChatBot />

      {/* Footer */}
      <Footer />
    </div>
  );
}
